import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';
import MicroSimPlayView from '../../components/microsim/MicroSimPlayView';
import MicroSimReport from '../../components/microsim/MicroSimReport';
import useWebSocket from '../../hooks/useWebSocket';
import useWakeLock from '../../hooks/useWakeLock';
import microSimService, { newTurnId } from '../../services/microSimService';
import tokenStorage from '../../utils/tokenStorage';

/**
 * A participant playing a micro simulation on their own phone.
 *
 * Unprotected route (participants have no account), but every API call carries this
 * session's signed participant token and the server decides who they are from it.
 *
 * State flow: GET state on load → server pushes `msim:state` to this participant's
 * private room after every change → choices and Continue are REST calls that return
 * the new state. A 10-second poll backs up the socket while waiting on the partner,
 * and a reload when the round-outcome countdown ends lets the server apply its
 * 60-second auto-advance.
 */

const POLL_MS = 10000;

const Shell = ({ children }) => (
  <div className="min-h-screen bg-white">
    <div className="sticky top-0 z-40 border-b border-neutral-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-xl items-center justify-between px-4 py-2.5">
        <div className="flex items-center">
          <img src={logoIcon} alt="Logo" className="h-7 w-7 object-contain" />
          <img src={logoFull} alt="Aktivity" className="h-7 object-contain" />
        </div>
        <span className="rounded-md bg-primary-50 px-2 py-1 text-[11px] font-bold text-primary-700">Micro simulation</span>
      </div>
    </div>
    {children}
  </div>
);

const MicroSimPlayPage = () => {
  const { sessionId, runId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [report, setReport] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(false);
  const pendingTurn = useRef(null);

  const hasToken = Boolean(tokenStorage.getParticipantToken(sessionId));
  const { on, off } = useWebSocket(hasToken ? sessionId : null);
  useWakeLock(Boolean(state) && ['in_progress', 'round_end'].includes(state?.status));

  const load = useCallback(async () => {
    try {
      const res = await microSimService.state(sessionId, runId);
      setState(res.state);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.userMessage || err.message || 'Could not load the micro simulation.');
    }
  }, [sessionId, runId]);

  useEffect(() => { if (hasToken) load(); }, [hasToken, load]);

  // Live updates pushed to this participant's verified room.
  useEffect(() => {
    if (!hasToken) return undefined;
    const onState = (payload) => {
      if (payload?.runId === runId) setState(payload);
    };
    const onAssigned = (payload) => {
      // Re-paired (e.g. the trainer reshuffled before anyone played).
      if (payload?.runId && payload.runId !== runId && String(payload.sessionId) === String(sessionId)) {
        navigate(`/session/${sessionId}/micro-sim/${payload.runId}`, { replace: true });
      }
    };
    const onEnded = () => load();
    on('msim:state', onState);
    on('msim:assigned', onAssigned);
    on('msim:ended', onEnded);
    return () => {
      off('msim:state', onState);
      off('msim:assigned', onAssigned);
      off('msim:ended', onEnded);
    };
  }, [hasToken, on, off, runId, sessionId, navigate, load]);

  // Backup poll while waiting for the other person.
  useEffect(() => {
    if (!state || state.status === 'completed' || state.status === 'abandoned') return undefined;
    if (state.status === 'in_progress' && state.turn?.isMine) return undefined;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [state, load]);

  // When the outcome countdown runs out, ask the server — it applies the auto-advance.
  useEffect(() => {
    if (state?.status !== 'round_end' || !state.roundEnd?.autoAdvanceAt) return undefined;
    const wait = new Date(state.roundEnd.autoAdvanceAt).getTime() - Date.now() + 500;
    const id = setTimeout(load, Math.max(wait, 500));
    return () => clearTimeout(id);
  }, [state?.status, state?.roundEnd?.autoAdvanceAt, load]);

  const adoptFromError = (err) => {
    if (err?.data?.state) setState(err.data.state);
    else setActionError(err?.userMessage || err?.message || 'Something went wrong. Please try again.');
  };

  const choose = async (optionId) => {
    if (!state) return;
    setBusy(true);
    setActionError(null);
    // Same id on a retry of the same turn, so the server records it once.
    const key = `${state.round}:${state.step}:${optionId}`;
    if (!pendingTurn.current || pendingTurn.current.key !== key) pendingTurn.current = { key, id: newTurnId() };
    try {
      const res = await microSimService.choose(sessionId, runId, {
        round: state.round, step: state.step, optionId, clientTurnId: pendingTurn.current.id,
      });
      pendingTurn.current = null;
      setState(res.state);
    } catch (err) {
      adoptFromError(err);
    } finally {
      setBusy(false);
    }
  };

  const continueRound = async () => {
    if (!state) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await microSimService.continueRound(sessionId, runId, state.round);
      setState(res.state);
    } catch (err) {
      adoptFromError(err);
    } finally {
      setBusy(false);
    }
  };

  const viewReport = useCallback(async () => {
    try {
      const res = await microSimService.report(sessionId, runId);
      setReport(res.report);
      window.scrollTo(0, 0);
    } catch (err) {
      setActionError(err?.userMessage || err?.message || 'Your results are not ready yet.');
    }
  }, [sessionId, runId]);

  // Opened from "Your practice results" (?view=report): go straight to the report.
  const wantsReport = searchParams.get('view') === 'report';
  const autoOpened = useRef(false);
  useEffect(() => {
    if (wantsReport && !autoOpened.current && state?.status === 'completed') {
      autoOpened.current = true;
      viewReport();
    }
  }, [wantsReport, state?.status, viewReport]);

  if (!hasToken) {
    return (
      <Shell>
        <div className="mx-auto max-w-sm px-6 py-16 text-center">
          <h1 className="text-lg font-bold text-neutral-900">Please rejoin the session</h1>
          <p className="mt-2 text-sm text-neutral-600">
            We need to confirm it’s you before you can take part. Rejoin with your session code and
            your participant code.
          </p>
          <Link to="/join" className="mt-5 inline-block rounded-xl bg-primary-500 px-5 py-3 font-bold text-white">
            Rejoin
          </Link>
        </div>
      </Shell>
    );
  }

  if (loadError && !state) {
    return (
      <Shell>
        <div className="mx-auto max-w-sm px-6 py-16 text-center">
          <h1 className="text-lg font-bold text-neutral-900">We couldn’t open this micro simulation</h1>
          <p className="mt-2 text-sm text-neutral-600">{loadError}</p>
          <Link to={`/session/${sessionId}/active`} className="mt-5 inline-block font-semibold text-primary-600">
            Back to the session
          </Link>
        </div>
      </Shell>
    );
  }

  if (!state) {
    return (
      <Shell>
        <div className="flex justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-neutral-200 border-t-primary-500" />
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {report ? (
        <>
          <MicroSimReport report={report} onDownloadPdf={() => microSimService.downloadMyReportPdf(sessionId, runId)} />
          <div className="mx-auto max-w-xl px-4 pb-10">
            <Link to={`/session/${sessionId}/active`} className="block w-full rounded-xl border border-neutral-300 py-3 text-center font-semibold text-neutral-800">
              Back to the session
            </Link>
          </div>
        </>
      ) : (
        <>
          <MicroSimPlayView
            state={state}
            onChoose={choose}
            onContinue={continueRound}
            onViewReport={viewReport}
            busy={busy}
            error={actionError}
          />
          {['completed', 'abandoned'].includes(state.status) && (
            <div className="mx-auto max-w-xl px-4 pb-10">
              <Link to={`/session/${sessionId}/active`} className="block w-full rounded-xl border border-neutral-300 py-3 text-center font-semibold text-neutral-800">
                Back to the session
              </Link>
            </div>
          )}
        </>
      )}
    </Shell>
  );
};

export default MicroSimPlayPage;
